import { Paper, Typography, Box, useTheme, useMediaQuery, IconButton } from "@mui/material";
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import FavoriteIcon from '@mui/icons-material/Favorite';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import BarChartIcon from '@mui/icons-material/BarChart';
import PokemonSprite from "./PokemonSprite";
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const ReplayCard = ({ game, showAnalyze = false, showAnalytics = false, onToggleAnalytics, isInAnalytics }) => {
  const { currentUser, savedReplaysIds, save, unsave } = useAuth();
  const theme = useTheme();
  const isXsScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isMdScreen = useMediaQuery(theme.breakpoints.between('md', 'lg'));
  const isLgScreen = useMediaQuery(theme.breakpoints.up('lg'));
  const navigate = useNavigate();

  const spriteSize = {
    xs: 40,
    sm: 45,
    md: 50,
    lg: 55
  };

  const getSpriteSizeForScreen = () => {
    if (isLgScreen) return spriteSize.lg;
    if (isMdScreen) return spriteSize.md;
    if (isXsScreen) return spriteSize.xs;
    return spriteSize.sm;
  };

  const isSaved = savedReplaysIds.includes(game.replay_id);

  const toggleSave = async e => {
    e.stopPropagation();
    if (!currentUser) return;
    try {
      if (isSaved) {
        await unsave(game.replay_id);
        // If this replay was also selected for Battle Analytics, deselect it
        if (isInAnalytics && onToggleAnalytics) {
          onToggleAnalytics(game.replay_id);
        }
      } else {
        await save(game);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const formatDate = (timestamp) => {
    try {
      const dateStr = typeof timestamp === 'object' && timestamp.value 
        ? timestamp.value 
        : timestamp;

      const date = new Date(dateStr);
      
      if (isNaN(date.getTime())) {
        console.error("Invalid date:", timestamp);
        return "Unknown";
      }

      return date.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch (error) {
      console.error("Error formatting date:", error);
      return "Unknown";
    }
  };

  const handleAnalyze = e => {
    e.stopPropagation();
    navigate(`/analyze-battle/${game.replay_id}`);
  };

  return (
    <Paper 
      onClick={(e) => {
        if (!e.target.closest('.MuiIconButton-root')) {
          window.open(`https://replay.pokemonshowdown.com/${game.replay_id}`, '_blank');
        }
      }}
      variant="replay"
      sx={{
        border: '1px solid rgba(255, 255, 255, 0.12)',
        transition: 'all 0.3s ease',
        width: '100%',
        maxWidth: '850px',
        mx: 'auto',
        '&:hover': {
          boxShadow: 3,
          borderColor: 'rgba(255, 255, 255, 0.3)',
          transform: 'translateY(-2px)',
        }
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          padding: { 
            xs: '1rem', 
            sm: '1.25rem 2rem', 
            md: '1.5rem 3rem' 
          },
        }}
      >
        {/* Teams Section (Top) */}
        <Box sx={{ 
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: 'center',
          justifyContent: 'center',
          gap: { xs: 2, sm: 4, md: 8, lg: 10 },
          width: '100%',
          mb: 3
        }}>
          {/* Team 1 */}
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(3, 1fr)',
            justifyContent: 'center',
            justifyItems: 'center',
            gap: { xs: 1, sm: 1.5, md: 2 },
          }}>
            {game.teams?.p1?.map((pokemon, index) => (
              <Box 
                key={`p1-${index}`} 
                sx={{ 
                  display: 'flex', 
                  justifyContent: 'center',
                  transform: { 
                    xs: 'scale(1)', 
                    sm: 'scale(1.1)', 
                    md: 'scale(1.2)', 
                    lg: 'scale(1.3)' 
                  }
                }}
              >
                <PokemonSprite 
                  pokemon={pokemon} 
                  size={getSpriteSizeForScreen()}
                />
              </Box>
            ))}
          </Box>

          {/* VS Divider or Just Space */}
          <Typography 
            variant="h6" 
            color="rgba(255,255,255,0.3)" 
            sx={{
              display: 'block',
              fontWeight: 'bold',
              fontSize: { xs: '1rem', sm: '1.25rem' },
              lineHeight: 1,
              my: { xs: 0.25, sm: 0 }
            }}
          >
            VS
          </Typography>

          {/* Team 2 */}
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(3, 1fr)',
            justifyContent: 'center',
            justifyItems: 'center',
            gap: { xs: 1, sm: 1.5, md: 2 },
          }}>
            {game.teams?.p2?.map((pokemon, index) => (
              <Box 
                key={`p2-${index}`} 
                sx={{ 
                  display: 'flex', 
                  justifyContent: 'center',
                  transform: { 
                    xs: 'scale(1)', 
                    sm: 'scale(1.1)', 
                    md: 'scale(1.2)', 
                    lg: 'scale(1.3)' 
                  }
                }}
              >
                <PokemonSprite 
                  pokemon={pokemon} 
                  size={getSpriteSizeForScreen()}
                />
              </Box>
            ))}
          </Box>
        </Box>

        {/* Info & Actions Section (Bottom) */}
        <Box 
          sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
            p: 2,
            bgcolor: 'rgba(0,0,0,0.2)',
            borderRadius: 2,
            gap: { xs: 2, md: 0 }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
            <Typography 
              variant="subtitle1"
              color="white" 
              sx={{ 
                fontWeight: 'bold',
                fontSize: { xs: '1rem', sm: '1.1rem', md: '1.2rem' }
              }}
            >
              {game.player1} vs {game.player2}
            </Typography>
          </Box>
          
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            flex: 1,
            gap: 3
          }}>
            <Typography 
              variant="subtitle2" 
              color="rgba(255,255,255,0.7)" 
              sx={{ fontSize: { xs: '0.85rem', sm: '0.95rem' } }}
            >
              Rating: {game.rating ? game.rating : "Unknown"}
            </Typography>
            
            <Typography 
              variant="subtitle2" 
              color="rgba(255,255,255,0.7)" 
              sx={{ fontSize: { xs: '0.85rem', sm: '0.95rem' } }}
            >
              {game.date ? formatDate(game.date) : "Unknown"}
            </Typography>
          </Box>

          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'flex-end',
            alignItems: 'center',
            flex: 1
          }}>
            {currentUser && (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {showAnalyze && showAnalytics && (
                  <IconButton
                    onClick={() => onToggleAnalytics(game.replay_id)}
                    title="Add to Battle Analytics"
                    sx={{
                      mr: 1,
                      color: isInAnalytics
                        ? theme.palette.success.main
                        : 'rgba(255,255,255,0.5)',
                      '&:hover': {
                        color: isInAnalytics
                          ? theme.palette.success.light
                          : 'rgba(255,255,255,0.8)'
                      }
                    }}
                  >
                    {isInAnalytics
                      ? <BarChartIcon sx={{ fontSize: 28 }} />
                      : <BarChartOutlinedIcon sx={{ fontSize: 28 }} />
                    }
                  </IconButton>
                )}
                {showAnalyze && (
                  <IconButton
                    onClick={handleAnalyze}
                    sx={{ mr: 1, color: 'white' }}
                    title="Analyze Battle"
                  >
                    <AnalyticsIcon sx={{ fontSize: { xs: 24, sm: 26, md: 28 } }} />
                  </IconButton>
                )}
                <IconButton
                  onClick={toggleSave}
                  sx={{
                    color: isSaved
                      ? theme.palette.error.main
                      : 'white'
                  }}
                  title={isSaved ? "Unsave Replay" : "Save Replay"}
                >
                  {isSaved
                    ? <FavoriteIcon sx={{ fontSize: { xs: 24, sm: 26, md: 28 } }} />
                    : <FavoriteBorderIcon sx={{ fontSize: { xs: 24, sm: 26, md: 28 } }} />
                  }
                </IconButton>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

export default ReplayCard;